import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Users } from '../api/types';
import config from '../config';

interface OPUsersParams {
  searchString: string;
  page: number;
  itemsPerPage: number;
  roles: Array<string>;
}

interface OPUsersParamsAxios extends OPUsersParams {
  axios: AxiosInstance;
}

async function getOPUsers({ axios, searchString, page, itemsPerPage, roles }: OPUsersParamsAxios): Promise<Users> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/users`, {
    params: {
      searchString: searchString || null,
      page,
      itemsPerPage,
      roles: roles.join(',') ?? null,
    },
  });

  return data;
}

export default function useOPUsersQuery({ searchString, page, itemsPerPage, roles }: OPUsersParams) {
  const axios = useAxios();
  const toast = useToast();
  const navigate = useNavigate();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({ searchString, page, itemsPerPage, roles }),
    queryFn: () => getOPUsers({ axios, searchString, page, itemsPerPage, roles }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.isError) {
      const error = query.error as any;
      if (error?.response?.status !== 503) {
        manageErrorResponse(toast, error);
      }
      if (error?.response?.status >= 500 && error?.response?.status !== 503) {
        navigate('/app/errorPage', { replace: true });
      }
    }
  }, [navigate, query.error, query.isError, toast]);

  return query;
}

function getKey({ searchString, page, itemsPerPage, roles }: { searchString?: string; page?: number; itemsPerPage?: number; roles?: Array<string> }) {
  return ['OPUsers', searchString, page, itemsPerPage, roles] as const satisfies QueryKey;
}
useOPUsersQuery.getKey = getKey;
